'use client';

import { Phone, Users, Grid3X3, Video } from 'lucide-react';

import { cn } from '@/lib/utils';

export interface TotalConnectIdleStageAsideProps {
  isDark: boolean;
  primaryColor: string;
  organizationName?: string | null;
  hasSipExtension: boolean;
  extensionNumber?: string;
}

/**
 * Wide-layout placeholder “stage” beside contacts / recents / keypad (desktop softphone lane).
 */
export function TotalConnectIdleStageAside({
  isDark,
  primaryColor,
  organizationName,
  hasSipExtension,
  extensionNumber,
}: TotalConnectIdleStageAsideProps) {
  const label = organizationName?.trim() || 'Total Connect';

  return (
    <div
      className={cn(
        'relative flex h-full min-h-[280px] flex-col items-center justify-center gap-8 overflow-hidden px-6 py-10 text-center',
        isDark
          ? 'bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950'
          : 'bg-gradient-to-br from-slate-50 via-white to-blue-50/40',
      )}
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.12]"
        style={{
          background: `radial-gradient(circle at 30% 20%, ${primaryColor}, transparent 55%), radial-gradient(circle at 70% 80%, ${primaryColor}, transparent 50%)`,
        }}
        aria-hidden
      />

      <div className="relative flex flex-col items-center gap-6">
        <div
          className={cn(
            'flex h-28 w-28 items-center justify-center rounded-full shadow-xl ring-2 ring-offset-4',
            isDark ? 'bg-gray-800 ring-gray-700 ring-offset-gray-950' : 'bg-white ring-gray-200 ring-offset-white',
          )}
          style={{ color: primaryColor }}
        >
          <Phone className="h-12 w-12" strokeWidth={1.5} aria-hidden />
        </div>

        <div className="max-w-sm space-y-2">
          <p
            className={cn(
              'text-xs font-semibold uppercase tracking-wider',
              isDark ? 'text-gray-500' : 'text-gray-400',
            )}
          >
            {label}
          </p>
          <h3
            className={cn(
              'text-xl font-bold leading-snug',
              isDark ? 'text-white' : 'text-gray-900',
            )}
          >
            Ready when you are
          </h3>
          <p
            className={cn(
              'text-sm leading-relaxed',
              isDark ? 'text-gray-400' : 'text-gray-600',
            )}
          >
            Choose a contact for a team call, open the dial pad for extensions and PSTN, or call back
            from recents.
          </p>
          {hasSipExtension && extensionNumber ? (
            <p
              className={cn(
                'mt-3 font-mono text-xs',
                isDark ? 'text-emerald-400/90' : 'text-emerald-700',
              )}
            >
              Ext. {extensionNumber} · inbound ready
            </p>
          ) : null}
        </div>

        <ul
          className={cn(
            'flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs',
            isDark ? 'text-gray-500' : 'text-gray-500',
          )}
        >
          <li className="flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5 shrink-0" aria-hidden />
            Team voice and video
          </li>
          <li className="flex items-center gap-1.5">
            <Grid3X3 className="h-3.5 w-3.5 shrink-0" aria-hidden />
            SIP keypad
          </li>
          <li className="flex items-center gap-1.5">
            <Video className="h-3.5 w-3.5 shrink-0" aria-hidden />
            Meetings
          </li>
        </ul>
      </div>
    </div>
  );
}
